import { useTheme } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import ProjectClientCard from './project-info-cards/ProjectClientCard';
import ExoTabs from '../../components/exo/ExoTabs';
import FinanzialTab from './project-info-tabs/FinancialTab';
import TeamTab from './project-info-tabs/TeamTab';
import ActionsTab from './project-info-tabs/ActionsTab';
import TimesTab from './project-info-tabs/TimesTab';
import { useTranslation } from 'react-i18next';
import OrdersTab from './project-info-tabs/OrdersTab';
import InvoicesTab from './project-info-tabs/InvoicesTab';
import DateLabel from '../../components/label/DateLabel';
import { hasPermissions } from '../../auth/hasPermissions';
import { ProjectStatus } from '../../components/special/Status';
import { ExpensesTab } from './project-info-tabs/ExpensesTab';
import ProjectControllingBars from '../project-controlling/ProjectControllingBars';
import CardContainer from '../../components/cards/layout/CardContainer';
import TimeCalculationTab from './project-info-tabs/TimeCalculationTab';
import CalculativeHourlyRateTab from './project-info-tabs/CalculativeHourlyRateTab';
import { BillingPositionsTab } from './project-info-tabs/BillingPositionsTab';
import { TravelExpensePresetsTab } from './project-info-tabs/TravelExpensePresetsTab';
import { LeadStatusSelector } from '../leads/status/LeadStatusSelector';

const ProjectInfo = ({ selectedProject, onDelete, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  function updateProject(data) {
    onChange({
      ...selectedProject,
      data: { ...selectedProject.data, ...data },
    });
  }

  return (
    <Box
      className="lg:h-full w-full rounded-b-lg lg:rounded-lg backdrop-blur-lg overflow-y-auto p-2 flex flex-col"
      sx={{
        backgroundColor: colors.glass,
      }}
    >
      {selectedProject && (
        <>
          <ProjectHead
            project={selectedProject.data}
            onStatusChange={(newStatus) => updateProject({ status: newStatus })}
          />
          <ProjectBody
            project={selectedProject.data}
            updateProject={updateProject}
            onDelete={onDelete}
          />
        </>
      )}
    </Box>
  );
};

const ProjectHead = ({ project, onStatusChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const [anchorEl, setAnchorEl] = useState(null);

  const statusOptions = [
    { label: t('Running'), status: 0, variant: 'open' },
    { label: t('Finished'), status: 1, variant: 'finished' },
    { label: t('Canceled'), status: 2, variant: 'rejected' },
  ];

  function handleStatusSelect(selected) {
    if (selected.status === project.status) return;
    DBRequest({
      config,
      path: 'projects/' + project.id,
      method: API_PATCH({ status: selected.status }),
      onResponse: () => onStatusChange(selected.status),
    });
  }

  return (
    <Box className="flex flex-col gap-2">
      <Box className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <ProjectClientCard project={project} />

        <CardContainer className="!items-start !gap-3">
          <Box className="flex items-center justify-between w-full">
            <Typography
              variant="h6"
              sx={{
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('Project Details')}
            </Typography>
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ cursor: 'pointer' }}
            >
              <ProjectStatus number={project.status} />
            </Box>
            <LeadStatusSelector
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              onStatusChange={handleStatusSelect}
              customStatuses={statusOptions}
            />
          </Box>
          <Box className="flex flex-col gap-2 w-full">
            <Box className="flex items-center justify-between">
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {t('Start Date')}
              </Typography>
              <Typography
                variant="body2"
                className="monospace"
                sx={{ fontWeight: 600 }}
              >
                <DateLabel date={project.startDate} />
              </Typography>
            </Box>
            <Box
              sx={{
                borderBottom: '1px solid',
                borderColor: colors.glass,
                opacity: 0.5,
              }}
            />
            <Box className="flex items-center justify-between">
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {t('Deadline')}
              </Typography>
              <Typography
                variant="body2"
                className="monospace"
                sx={{ fontWeight: 600 }}
              >
                <DateLabel date={project.deadline} />
              </Typography>
            </Box>
          </Box>
        </CardContainer>

        <CardContainer
          sx={{ '& > *': { width: '100%', maxWidth: 'unset', ml: 0 } }}
        >
          <ProjectControllingBars row={project} />
        </CardContainer>
      </Box>
    </Box>
  );
};

const ProjectBody = ({ project, updateProject, onDelete }) => {
  const { t } = useTranslation();
  function handleTeamChange(newMembers) {
    updateProject({ teamMembers: newMembers });
  }
  function handleStatusChange(newStatus) {
    updateProject({ status: newStatus });
  }

  const tabs = [
    {
      label: t('Financial'),
      content: <FinanzialTab project={project} />,
      hidden: !hasPermissions('projects-finance'),
    },
    {
      label: t('Expenses'),
      content: <ExpensesTab project={project} />,
      hidden: !hasPermissions('projects-finance'),
    },
    {
      label: t('Destinations'),
      content: <TravelExpensePresetsTab project={project} />,
      hidden:
        !hasPermissions('projects-finance') || !hasPermissions('travel-expense'),
    },
    {
      label: t('Times'),
      content: <TimesTab project={project} />,
      hidden: !hasPermissions('projects-times'),
    },
    {
      label: t('Billable Positions'),
      content: <BillingPositionsTab project={project} />,
      hidden: !hasPermissions('projects-times'),
    },
    {
      label: t('Orders'),
      content: <OrdersTab project={project} />,
      hidden: !hasPermissions('projects-orders'),
    },
    {
      label: t('Invoices'),
      content: <InvoicesTab project={project} />,
      hidden: !hasPermissions('projects-invoices'),
    },
    {
      label: t('Calculative Hourly Rate'),
      content: <CalculativeHourlyRateTab project={project} />,
    },
    {
      label: t('Time calculations'),
      content: <TimeCalculationTab project={project} />,
    },
    {
      label: t('Actions'),
      content: (
        <ActionsTab
          project={project}
          onDelete={onDelete}
          onStatusChange={handleStatusChange}
        />
      ),
      hidden: !hasPermissions('projects-actions'),
    },
  ];
  return (
    <ExoTabs
      noBackground
      tabs={tabs}
      sx={{
        paddingTop: '20px',
      }}
      className="w-full h-full flex flex-col rounded-lg overflow-hidden"
    />
  );
};

export default ProjectInfo;
